import React, { useEffect } from "react";
import { endpoints } from "config";
import { useLocation } from "react-router-dom";
import DeleteModal from "components/helpers/DeleteModal";
import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import { CardListPage, NestedListItemProps, } from "components/Category/Category";
import { parseListDate } from "helpers/parseListDate";
import { ColumnTypes } from "components/newFlexTable/FlexTable";

import View from "assets/Icon_Action_01.png";
import Update from "assets/Icon_Action_02.png";
import Delete from "assets/Icon_Action_03.png";

const SnaggingAsset = lazyWithRetry(() => import("./SnaggingAsset"));

export type Data = {
  snagging__Id: number;
  snagging__ProjectsID: number;
  snagging__AssetsId: number | null;
  snagging__AssetName: string | null;
  snagging__SnagDescription: string;
  snagging__Quantity: number;
  reasonCode__Name: string | null;
  suppliers__CompaniesName: string | null;
  snagging__AssignedTo: string | null;
  snagging__ForecastDate: string | null;
  snagging__CompletedDate: string | null;
  snagging__CancelledDate: string | null;
  snagging__bActive: boolean;
  snagging__AddDate: string | null;
  snagging__AddedBy: string | null;
  snagging__EditDate: string | null;
  snagging__LastEditedBy: string | null;
};

export const idColumn = "snagging__Id";

const getCrudItems = (
  nestedListItemProps?: NestedListItemProps,
  parentSelectedId?: number,
  parentSelectedIdName?: string,
  lowerCaseParentSelectedIdName?: string
): CrudItems<Data> => {
  return [
    {
      icon: View,
      title: "View punch / snagging",
      imgAltText: "View",
      permissionsKey: parentSelectedId ? "snaggingassets-view" : "view",
      menuCode: "VIEW",
      showInMultiSelect: false,
      content: (
        onDone,
        selectedData,
        menu,
        additionalData,
        clickedIdIfMultiIds
      ) => (
        <SnaggingAsset
          onDone={onDone}
          selectedData={selectedData}
          idFromRowOption={menu.selectedId}
          actionType="view"
          submitButtonText="Close"
          hideActionButton={true}
          nestedListPageUrl={nestedListItemProps?.urls?.view ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
          autoMenuActionsCode={"VIEW"}
          clickedIdIfMultiIds={clickedIdIfMultiIds}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: View,
      title: "Create punch / snagging",
      imgAltText: "Create",
      menuCode: "CREATE",
      showInMultiSelect: false,
      permissionsKey: parentSelectedId ? "snaggingassets-create" : "create",
      content: (onDone, selectedData, menu, additionalData, clickedIdIfMultiIds) => (
        <SnaggingAsset
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="create"
          submitButtonText="Add"
          nestedListPageUrl={nestedListItemProps?.urls?.create ?? undefined}
          parentSelectedIdName={parentSelectedIdName}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
      notInMenus: true,
    },
    {
      icon: Update,
      imgAltText: "Update",
      permissionsKey: parentSelectedId ? "snaggingassets-edit" : "edit",
      title: "Update punch / snagging",
      menuCode: "UPDATE",
      showInMultiSelect: false,
      content: (onDone, selectedData, menu) => (
        <SnaggingAsset
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="edit"
          submitButtonText="Update"
          nestedListPageUrl={nestedListItemProps?.urls?.edit ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: Delete,
      imgAltText: "Delete",
      permissionsKey: parentSelectedId ? "snaggingassets-delete" : "delete",
      title: data =>
        data.length <= 1
          ? "Delete punch / snagging"
          : `Delete ${data.length} punch / snaggings`,

      menuCode: "DELETE",
      showInMultiSelect: true,
      content: (onActionComplete, selectedData, menu) => {
        return (
          <DeleteModal
            url={
              nestedListItemProps?.urls?.delete ??
              endpoints.snagging.delete
            }
            noun="punch / snagging"
            pluralNoun="punch / snaggings"
            selectedIds={selectedData.map(d => d.snagging__Id)}
            renderListItem={data => {
              return (
                <li>{`${data.snagging__Id} - ${data.snagging__AssetsId != 0 ? data.snagging__AssetName : data.snagging__SnagDescription}`}</li>
              );
            }}
            selectedData={selectedData}
            open={menu.currentMenu === "DELETE"}
            close={onActionComplete}
            idFromRowOption={menu.selectedId}
            parentSelectedIdName={lowerCaseParentSelectedIdName ?? undefined}
            parentSelectedId={parentSelectedId ?? undefined}
          />
        );
      },
      inRightPanel: false,
      isDisabled: false,
    },
  ];
};

const rowFormatter = (
  row: Data,
  columnTypes?: ColumnTypes
): { [K in keyof typeof row]: React.ReactNode } => {
  let formattedRow: any = { ...row };

  let formattedColumns: string[] = [];

  Object.keys(row).forEach(columnKey => {
    if (
      !formattedColumns.includes(columnKey) &&
      columnTypes?.hasOwnProperty(columnKey)
    ) {
      const columnType = columnTypes[columnKey];

      if (columnType === "DateTime") {
        if (formattedRow[columnKey]) {
          let tempFormattedRow = { ...formattedRow };
          tempFormattedRow[columnKey] = parseListDate(
            formattedRow[columnKey],
            false
          );
          formattedRow = tempFormattedRow;
          formattedColumns.push(columnKey);
        }
      }
    }
  });

  return formattedRow;
};

export interface SnaggingAssetsProps {
  setTestRedirected?: React.Dispatch<
    React.SetStateAction<boolean | null | undefined>
  >;
  testRedirected?: boolean | null;
  parentSelectedId?: number;
  nestedListItemProps?: NestedListItemProps;
  parentSelectedIdName?: string;
  lowerCaseParentSelectedIdName?: string;
  hideCardPage?: boolean;
}

export type Props = SnaggingAssetsProps;

export type DisableMenuItemData = {
  [key: string]: { fieldName: string; fieldValue: any };
};

const SnaggingAssets: React.FC<Props> = props => {
  const location = useLocation();

  // const resendVerificationLinkStatus = useSelector(
  //   selectResendVerificationLinkStatus
  // );

  // const dispatch = useDispatch();

  // useEffect(() => {
  //   if (
  //     resendVerificationLinkStatus ||
  //     resendVerificationLinkStatus === false
  //   ) {
  //     dispatch(clearResendVerificationLinkStatus());
  //   }
  // }, [resendVerificationLinkStatus]);

  const crudItems = getCrudItems(
    props.nestedListItemProps,
    props.parentSelectedId,
    props.parentSelectedIdName,
    props.lowerCaseParentSelectedIdName
  );

  // Define the endpoint url for fetching table data.
  const url = props.nestedListItemProps?.urls?.list ?? endpoints.documents.list;
  let csvUrl = "";
  if (
    props.nestedListItemProps?.endpointDataToSend?.get?.listCsv?.selectedParentId &&
    props.parentSelectedIdName &&
    props.nestedListItemProps?.urls?.listCsv
  ) {
    csvUrl = `${props.nestedListItemProps.urls.listCsv}?${props.nestedListItemProps.endpointDataToSend.get.listCsv.selectedParentId}=${props.parentSelectedId}`;
  } else if (props.parentSelectedIdName && props.nestedListItemProps?.urls?.listCsv) {
    csvUrl = props.nestedListItemProps.urls.listCsv;
  } else {
    csvUrl = endpoints.snagging.listCsv;
  }

  return (
    <div className="SnaggingAssetsPage CardListPage">
      <Category<Data>
        location={location}
        noun="punch / snagging"
        createPermissionsKey={
          props.parentSelectedId ? "snaggingassets-create" : "create"
        }
        idColumn={idColumn}
        listEndpointUrl={url}
        defaultBActiveFilterColumnKey={"Snagging__bActive"}
        rowFormatter={rowFormatter}
        hiddenColumns={[idColumn]}
        showAddBulk={"Upload punch / snagging"}
        listCSV={{ url: csvUrl, noun: "Punch_Snagging assets" }}
        crudComponents={crudItems}
        hideHeaderTitle={false}
        overridePageTitle={
          props.parentSelectedId ? "Punch / Snagging" : undefined
        }
        hideCardPage={true}
        defaultCardOrListView={CardListPage.List}
        largeRightPanel={menuCode =>
          menuCode === "CREATE" || menuCode === "VIEW" || menuCode === "UPDATE"
        }
        rowCellOptions={{
          snagging__AssetName: { width: "SemiLarge2" },
          snagging__SnagDescription: { width: "SemiLarge3" },
          snagging__Quantity: { width: "Medium2" },
          reasonCode__Name: { width: "SemiLarge2" },
          snaggingCategories__Name: { width: "SemiLarge2" },
          snagging__AssignedTo: { width: "SemiLarge" },
          snagging__AddDate: { width: "SemiLarge" },
          snagging__ForecastDate: { width: "Medium2" },
          snagging__CompletedDate: { width: "SemiLarge" },
          snagging__CancelledDate: { width: "Medium2" },
        }}
        nestedListItemProps={props.nestedListItemProps}
        parentSelectedId={props.parentSelectedId}
      />
    </div>
  );
};

export default SnaggingAssets;
